import { z } from 'zod';

/**
 * THE BLOCK-LIBRARY MIRROR (D-page-schema-editor-8a).
 *
 * A FAITHFUL Zod copy of the platform block contract (`@cashblack/contracts`
 * blocks.ts, v1.1, 9 types). The real shared package is the cross-repo SWAP SEAM
 * (the same posture the data provider takes toward the real admin API and the
 * rate-rule resolver takes toward `@cashblack/contracts/rate-rules`): at migration
 * this file is DELETED and the imports point at the real package, e.g.
 *
 *   import { BlockSchema, PageSchemaSchema, type Block } from '@cashblack/contracts/blocks';
 *
 * The admin repo is separate, so the contract is not a dependency here; this is the
 * faithful mirror, pinned by fidelity tests. The apps' renderer consumes the SAME
 * discriminated union with an exhaustive switch that skips unknown types, so the
 * admin authors only shapes the renderer accepts, and the publish-time validator
 * (sub-surface 8b) mirrors the SAME schemas.
 *
 * MIRROR NOTES (match the contract, do not invent or paraphrase):
 *  - Every block object and every block `data` object is `.strict()` (no extra keys),
 *    so a typo or a stale field is rejected, not silently carried.
 *  - The TWO link shapes are kept DISTINCT: a hero CTA carries a plain STRING link
 *    (CtaSchema), while banner and pillCta carry the structured {kind, ref} object
 *    (LinkSchema). They are different shapes and both are preserved.
 *  - carousel.variant, categoryStrip.variant and banner.variant are all REQUIRED
 *    (v1.1 renamed the old carousel `cardStyle` to `variant` and made the three
 *    variants required). A mirror built from the old v1 shapes would emit the wrong
 *    field name or omit a required one, and the v1.1 apps would reject / mis-render.
 *
 * CROSS-REPO IMPORT MAPPING:
 *  - The contract's `MarketCodeSchema` maps onto the admin's existing `Market`
 *    (uk / us / ie), so it is mirrored locally as the SAME three-value enum below
 *    rather than re-invented. The inferred type equals the admin `Market`.
 *  - The contract's `BoostSchema` is referenced only by the contract's Store /
 *    Channel shapes, NOT by any block `data` shape, so it is NOT mirrored here:
 *    blocks reference stores and categories BY id / slug, resolved from the admin
 *    catalog (merchants, categories), never by an embedded Store / Channel object.
 */

/** The nine block types (v1.1). The renderer switches exhaustively over these. */
export const BlockTypeSchema = z.enum([
  'hero',
  'carousel',
  'storeGrid',
  'categoryStrip',
  'banner',
  'featuredStore',
  'textBlock',
  'divider',
  'pillCta',
]);
export type BlockType = z.infer<typeof BlockTypeSchema>;

/**
 * Market code (mirror of the contract `MarketCodeSchema`). Maps onto the admin
 * `Market` (uk / us / ie); the inferred type is the same three values.
 */
export const MarketCodeSchema = z.enum(['uk', 'us', 'ie']);

/**
 * A structured link target ({kind, ref}) used by banner.link and pillCta.link.
 * `ref` is a store slug, a category slug, a page id or an external url depending
 * on `kind`. DISTINCT from CtaSchema (the hero cta carries a plain string link).
 */
export const LinkSchema = z
  .object({
    kind: z.enum(['store', 'category', 'page', 'external']),
    ref: z.string(),
  })
  .strict();
export type Link = z.infer<typeof LinkSchema>;

/**
 * A hero call-to-action: a label plus a plain STRING link. DISTINCT from
 * LinkSchema (the {kind, ref} object banner / pillCta carry).
 */
export const CtaSchema = z
  .object({
    label: z.string(),
    link: z.string(),
  })
  .strict();
export type Cta = z.infer<typeof CtaSchema>;

// --- the nine block schemas (each .strict(), each data .strict()) -----------

export const HeroBlockSchema = z
  .object({
    type: z.literal('hero'),
    id: z.string(),
    data: z
      .object({
        image: z.string(),
        title: z.string(),
        subtitle: z.string().optional(),
        cta: CtaSchema.optional(),
      })
      .strict(),
  })
  .strict();

export const CarouselBlockSchema = z
  .object({
    type: z.literal('carousel'),
    id: z.string(),
    data: z
      .object({
        // REQUIRED in v1.1 (renamed from the old `cardStyle`).
        variant: z.enum(['store', 'category', 'promo', 'seasonal']),
        title: z.string().optional(),
        storeIds: z.array(z.string()).optional(),
        categoryIds: z.array(z.string()).optional(),
      })
      .strict(),
  })
  .strict();

export const StoreGridBlockSchema = z
  .object({
    type: z.literal('storeGrid'),
    id: z.string(),
    data: z
      .object({
        title: z.string().optional(),
        storeIds: z.array(z.string()).min(1),
        columns: z.union([z.literal(2), z.literal(3)]),
      })
      .strict(),
  })
  .strict();

export const CategoryStripBlockSchema = z
  .object({
    type: z.literal('categoryStrip'),
    id: z.string(),
    data: z
      .object({
        // REQUIRED in v1.1.
        variant: z.enum(['standard', 'row']),
        categoryIds: z.array(z.string()).min(1),
      })
      .strict(),
  })
  .strict();

export const BannerBlockSchema = z
  .object({
    type: z.literal('banner'),
    id: z.string(),
    data: z
      .object({
        // REQUIRED in v1.1.
        variant: z.enum(['standard', 'promo']),
        image: z.string(),
        link: LinkSchema,
        aspectRatio: z.string().optional(),
      })
      .strict(),
  })
  .strict();

export const FeaturedStoreBlockSchema = z
  .object({
    type: z.literal('featuredStore'),
    id: z.string(),
    data: z
      .object({
        storeId: z.string(),
        headline: z.string().optional(),
      })
      .strict(),
  })
  .strict();

export const TextBlockSchema = z
  .object({
    type: z.literal('textBlock'),
    id: z.string(),
    data: z
      .object({
        title: z.string().optional(),
        body: z.string().min(1),
        align: z.enum(['left', 'center']).optional(),
      })
      .strict(),
  })
  .strict();

export const DividerBlockSchema = z
  .object({
    type: z.literal('divider'),
    id: z.string(),
    data: z
      .object({
        style: z.enum(['thin', 'thick', 'space']),
      })
      .strict(),
  })
  .strict();

export const PillCtaBlockSchema = z
  .object({
    type: z.literal('pillCta'),
    id: z.string(),
    data: z
      .object({
        label: z.string(),
        link: LinkSchema,
        icon: z.string().optional(),
        tone: z.enum(['primary', 'secondary', 'subtle']).optional(),
      })
      .strict(),
  })
  .strict();

/**
 * The block contract: a discriminated union over `type`. This is the exact shape
 * the apps' exhaustive-switch renderer consumes and the publish-time validator
 * (8b) checks against.
 */
export const BlockSchema = z.discriminatedUnion('type', [
  HeroBlockSchema,
  CarouselBlockSchema,
  StoreGridBlockSchema,
  CategoryStripBlockSchema,
  BannerBlockSchema,
  FeaturedStoreBlockSchema,
  TextBlockSchema,
  DividerBlockSchema,
  PillCtaBlockSchema,
]);
export type Block = z.infer<typeof BlockSchema>;

/**
 * The PUBLISHED page schema the apps consume (the contract shape). `version` is a
 * positive int and `publishedAt` an ISO datetime, both SET AT PUBLISH (8b). The
 * admin's STORED draft row (`PageSchema` in dataProvider/types.ts) is a superset
 * carrying the RaRecord id, the draft status and authoring timestamps; for a draft
 * version / publishedAt are placeholders, so a draft is validated per-BLOCK against
 * BlockSchema, not against this whole-schema shape (that is the 8b publish gate).
 */
export const PageSchemaSchema = z
  .object({
    pageId: z.string(),
    market: MarketCodeSchema,
    version: z.number().int().positive(),
    publishedAt: z.iso.datetime(),
    blocks: z.array(BlockSchema),
  })
  .strict();
export type PageSchemaContract = z.infer<typeof PageSchemaSchema>;
